import { COLORS, DISABLED_BTN } from "../shared/constants";
import { IconSearch, IconChevron, IconAlert, IconX, IconEdit } from "../shared/icons";
import { canDo, formatDate, getUserName } from "../shared/utils";
import { useState, useEffect } from "react";

export default // ─── Patient List ────────────────────────────────────────────
function PatientList({ patients, providers, users, treatments, reminders, currentUser, onNavigate, onSelectPatient, onEditPatient }) {
  const [search, setSearch] = useState("");
  const storageKey = currentUser ? `patientListSort_${currentUser.id}` : null;
  const savedSort = (() => {
    try {
      if (storageKey) {
        const s = JSON.parse(localStorage.getItem(storageKey));
        if (s && s.field && s.dir) return s;
      }
    } catch (e) {}
    return { field: "last_name", dir: "asc" };
  })();
  const [sortField, setSortField] = useState(savedSort.field);
  const [sortDir, setSortDir] = useState(savedSort.dir);
  const [statusFilter, setStatusFilter] = useState("ALL");

  useEffect(() => {
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify({ field: sortField, dir: sortDir })); } catch (e) {}
    }
  }, [sortField, sortDir, storageKey]);

  const getProviderName = (id) => {
    const p = providers.find((pv) => pv.id === id);
    return p ? p.name : "—";
  };

  const getActiveProtocols = (patientId) => {
    // First check if patient object has active_protocols from the API (available on list load)
    const pat = patients.find((p) => p.id === patientId);
    if (pat && pat.active_protocols && pat.active_protocols.length > 0) {
      return pat.active_protocols;
    }
    // Fall back to treatments state (populated after clicking into a patient)
    return (treatments || []).filter((t) => t.patient_id === patientId && t.status === "ACTIVE").map((t) => t.protocol_name);
  };

  const getDueReminderColors = (patientId) => {
    const patientTxIds = (treatments || []).filter((t) => t.patient_id === patientId && t.status === "ACTIVE").map((t) => t.id);
    const dueReminders = (reminders || []).filter((r) => patientTxIds.includes(r.treatment_assignment_id) && r.is_active && r.status === "DUE");
    const colors = [...new Set(dueReminders.map((r) => r.color))];
    return colors;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const filtered = patients
    .filter((p) => {
      if (statusFilter !== "ALL" && p.status !== statusFilter) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        p.first_name.toLowerCase().includes(s) ||
        p.last_name.toLowerCase().includes(s) ||
        String(p.patient_number).includes(s) ||
        (p.diagnosis_code || "").toLowerCase().includes(s) ||
        getActiveProtocols(p.id).some((pr) => pr.toLowerCase().includes(s))
      );
    })
    .sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case "patient_number": aVal = a.patient_number; bVal = b.patient_number; break;
        case "first_name": aVal = a.first_name.toLowerCase(); bVal = b.first_name.toLowerCase(); break;
        case "last_name": aVal = a.last_name.toLowerCase(); bVal = b.last_name.toLowerCase(); break;
        case "date_of_birth": aVal = a.date_of_birth; bVal = b.date_of_birth; break;
        case "last_session_date": aVal = a.last_session_date || ""; bVal = b.last_session_date || ""; break;
        case "treatment_number": aVal = a.treatment_number || 0; bVal = b.treatment_number || 0; break;
        case "last_treated_by": aVal = getUserName(users, a.last_treated_by); bVal = getUserName(users, b.last_treated_by); break;
        case "active_protocols": aVal = getActiveProtocols(a.id).join(", ").toLowerCase(); bVal = getActiveProtocols(b.id).join(", ").toLowerCase(); break;
        case "reminder_due": aVal = getDueReminderColors(a.id).length; bVal = getDueReminderColors(b.id).length; break;
        case "status": aVal = a.status || ""; bVal = b.status || ""; break;
        default: aVal = a.last_name.toLowerCase(); bVal = b.last_name.toLowerCase();
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const SortHeader = ({ field, label, width }) => (
    <th
      onClick={() => handleSort(field)}
      style={{
        padding: "12px 14px",
        textAlign: "left",
        fontSize: "12px",
        fontWeight: "600",
        color: COLORS.textSecondary,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        cursor: "pointer",
        userSelect: "none",
        borderBottom: `2px solid ${COLORS.border}`,
        width: width || "auto",
        whiteSpace: "nowrap",
        background: sortField === field ? COLORS.primaryLight : "transparent",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
        {label}
        {sortField === field && <IconChevron dir={sortDir === "asc" ? "up" : "down"} />}
      </span>
    </th>
  );

  const statusBadge = (status) => {
    const cfg = {
      ACTIVE: { bg: "#E8F8F0", color: COLORS.badgeActive, label: "Active" },
      COMPLETED: { bg: "#ECEDF0", color: COLORS.badgeCompleted, label: "Completed" },
    };
    const c = cfg[status] || cfg.ACTIVE;
    return (
      <span style={{
        padding: "3px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: "600",
        background: c.bg,
        color: c.color,
      }}>
        {c.label}
      </span>
    );
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Toolbar */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "20px",
        gap: "16px",
        flexWrap: "wrap",
      }}>
        <h2 style={{ color: COLORS.text, fontSize: "20px", fontWeight: "600", margin: 0 }}>
          Patients
          <span style={{ color: COLORS.textSecondary, fontWeight: "400", fontSize: "14px", marginLeft: "8px" }}>
            ({filtered.length})
          </span>
        </h2>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {/* Search */}
          <div style={{
            display: "flex",
            alignItems: "center",
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: "8px",
            padding: "0 12px",
            height: "40px",
            width: "260px",
          }}>
            <IconSearch />
            <input
              type="text"
              placeholder="Search by name, ID, or diagnosis…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                border: "none",
                outline: "none",
                background: "transparent",
                marginLeft: "8px",
                fontSize: "14px",
                width: "100%",
                color: COLORS.text,
              }}
            />
            {search && (
              <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", color: COLORS.textSecondary }}>
                <IconX />
              </button>
            )}
          </div>
          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              height: "40px",
              padding: "0 12px",
              borderRadius: "8px",
              border: `1px solid ${COLORS.border}`,
              background: COLORS.surface,
              fontSize: "13px",
              color: COLORS.text,
              cursor: "pointer",
            }}
          >
            <option value="ALL">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="COMPLETED">Completed</option>
          </select>
          {/* New Patient */}
          <button
            onClick={canDo(currentUser, "create_edit_patients") ? () => onNavigate("NEW_PATIENT") : undefined}
            style={{
              height: "40px",
              padding: "0 18px",
              borderRadius: "8px",
              border: "none",
              background: COLORS.accent,
              color: "#FFF",
              fontSize: "13px",
              fontWeight: "600",
              cursor: canDo(currentUser, "create_edit_patients") ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              whiteSpace: "nowrap",
              ...(canDo(currentUser, "create_edit_patients") ? {} : DISABLED_BTN),
            }}
          >
            + New Patient
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{
        background: COLORS.surface,
        borderRadius: "12px",
        border: `1px solid ${COLORS.borderLight}`,
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
      }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <SortHeader field="patient_number" label="ID" width="70px" />
                <th onClick={() => handleSort("reminder_due")}
                  style={{ padding: "12px 8px", textAlign: "center", fontSize: "14px", borderBottom: `2px solid ${COLORS.border}`, width: "40px", cursor: "pointer", userSelect: "none", background: sortField === "reminder_due" ? COLORS.primaryLight : "transparent" }}
                  title="Reminders Due — click to sort">
                  🔔{sortField === "reminder_due" && <span style={{ fontSize: "10px", marginLeft: "2px" }}><IconChevron dir={sortDir === "asc" ? "up" : "down"} /></span>}
                </th>
                <SortHeader field="first_name" label="First Name" />
                <SortHeader field="last_name" label="Last Name" />
                <SortHeader field="date_of_birth" label="DOB" width="120px" />
                <SortHeader field="last_session_date" label="Last Tx" width="110px" />
                <SortHeader field="treatment_number" label="Tx #" width="70px" />
                <th style={{ padding: "12px 14px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: `2px solid ${COLORS.border}`, width: "140px" }}>Last Treated By</th>
                <SortHeader field="active_protocols" label="Active Protocol(s)" />
                <SortHeader field="status" label="Status" width="100px" />
                <th style={{ padding: "12px 14px", borderBottom: `2px solid ${COLORS.border}`, width: "50px" }} />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ padding: "40px", textAlign: "center", color: COLORS.textSecondary }}>
                    No patients found.
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr
                    key={p.id}
                    style={{
                      borderBottom: `1px solid ${COLORS.borderLight}`,
                      cursor: "pointer",
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = COLORS.primaryLight)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    onClick={() => onSelectPatient(p)}
                  >
                    <td style={{ padding: "14px", fontSize: "13px", color: COLORS.textSecondary, fontWeight: "500" }}>
                      {p.patient_number}
                    </td>
                    <td style={{ padding: "6px 8px", textAlign: "center" }}>
                      {(() => {
                        const dueColors = getDueReminderColors(p.id);
                        const dotColorMap = { yellow: "#D4A800", blue: "#1A56DB", orange: "#D4700A" };
                        return dueColors.length > 0 ? (
                          <div style={{ display: "flex", gap: "3px", justifyContent: "center" }}>
                            {dueColors.map((c, i) => (
                              <div key={i} style={{ width: "10px", height: "10px", borderRadius: "50%", background: dotColorMap[c] || dotColorMap.orange }} />
                            ))}
                          </div>
                        ) : null;
                      })()}
                    </td>
                    <td style={{ padding: "14px", fontSize: "14px", fontWeight: "500", color: COLORS.text }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                        {p.first_name}
                        {p.precautions && (
                          <span style={{ color: COLORS.danger }} title={p.precautions}>
                            <IconAlert />
                          </span>
                        )}
                      </span>
                    </td>
                    <td style={{ padding: "14px", fontSize: "14px", fontWeight: "500", color: COLORS.text }}>
                      {p.last_name}
                    </td>
                    <td style={{ padding: "14px", fontSize: "13px", color: COLORS.textSecondary }}>
                      {formatDate(p.date_of_birth)}
                    </td>
                    <td style={{ padding: "14px", fontSize: "13px", color: COLORS.textSecondary }}>
                      {formatDate(p.last_session_date)}
                    </td>
                    <td style={{ padding: "14px", fontSize: "13px", color: COLORS.textSecondary, textAlign: "center" }}>
                      {p.treatment_number || "—"}
                    </td>
                    <td style={{ padding: "14px", fontSize: "13px", color: COLORS.textSecondary }}>
                      {getUserName(users, p.last_treated_by)}
                    </td>
                    <td style={{ padding: "14px" }}>
                      {(() => {
                        const ap = getActiveProtocols(p.id);
                        return ap.length > 0 ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                            {ap.map((proto, i) => (
                              <span key={i} style={{
                                padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: "600",
                                background: COLORS.primaryLight, color: COLORS.primary, whiteSpace: "nowrap",
                              }}>
                                {proto}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{ fontSize: "12px", color: COLORS.textSecondary }}>—</span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: "14px" }}>
                      {statusBadge(p.status)}
                    </td>
                    <td style={{ padding: "14px", textAlign: "center" }}>
                      <span style={{ color: COLORS.textSecondary, cursor: canDo(currentUser, "create_edit_patients") ? "pointer" : "not-allowed", ...(canDo(currentUser, "create_edit_patients") ? {} : DISABLED_BTN) }} onClick={(e) => { e.stopPropagation(); if (canDo(currentUser, "create_edit_patients")) onEditPatient(p); }}><IconEdit /></span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Patient Form (Create / Edit) ───────────────────────────

